import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useCancelApprovalRequest,
  useDeliverableSetsStore,
} from '@/features/deliverable-set'

import { ErrorMessage } from './ErrorMessage'
import { NoteField } from './NoteField'

export interface CancelApprovalRequestFormData {
  note?: string
}

const CancelApprovalRequestDialog: React.FC = () => {
  const { t } = useTranslation()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const {
    showCancelApprovalDialog,
    cancelApprovalDeliverableSet,
    setCancelApprovalDialogOpen,
    clearCancelApprovalDeliverableSet,
  } = useDeliverableSetsStore()

  const cancelApprovalMutation = useCancelApprovalRequest()
  const isSaving = cancelApprovalMutation.isPending

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CancelApprovalRequestFormData>({
    defaultValues: { note: '' },
    mode: 'onChange',
  })

  const noteWatch = watch('note')

  useEffect(() => {
    if (!showCancelApprovalDialog) {
      reset({ note: '' })
      setErrorMsg(null)
    }
  }, [showCancelApprovalDialog, reset])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setCancelApprovalDialogOpen(open)
      if (!open) {
        clearCancelApprovalDeliverableSet()
        setErrorMsg(null)
        reset({ note: '' })
      }
    },
    [setCancelApprovalDialogOpen, clearCancelApprovalDeliverableSet, reset]
  )

  const onSubmit = (data: CancelApprovalRequestFormData) => {
    if (!cancelApprovalDeliverableSet) return

    setErrorMsg(null)

    cancelApprovalMutation.mutate(
      {
        id: cancelApprovalDeliverableSet.id,
        data: { note: data?.note },
      },
      {
        onSuccess: () => {
          triggerToast(
            'success',
            t('deliverableSet.cancelApprovalRequest.success')
          )
          handleOpenChange(false)
        },
        onError: () => {
          triggerToast('error', t('deliverableSet.cancelApprovalRequest.error'))
        },
      }
    )
  }

  return (
    <Dialog open={showCancelApprovalDialog} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="!overflow-hidden flex flex-col">
          <DialogHeader className="space-y-0.5 flex-shrink-0">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('deliverableSet.cancelApprovalRequest.title')}
            </DialogTitle>
            <DialogDescription>
              {t('deliverableSet.cancelApprovalRequest.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 px-6 -mx-6 overflow-x-hidden overflow-y-auto">
            <ErrorMessage message={errorMsg || ''} />

            <form
              id="cancel-approval-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <NoteField
                control={control}
                name="note"
                disabled={isSaving}
                errors={errors}
                noteValue={noteWatch}
                isOptional
              />
            </form>
          </div>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3 flex-shrink-0">
            <Button
              type="button"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white text-[--Colors-Gray-light-700]"
            >
              {t('deliverableSet.cancelApprovalRequest.actions.keepRequest')}
            </Button>

            <Button
              type="submit"
              form="cancel-approval-form"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent text-white bg-error-solid hover:bg-error-solid-hover"
              variant="default"
              disabled={isSaving}
            >
              {t('deliverableSet.cancelApprovalRequest.actions.cancelRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default CancelApprovalRequestDialog
